import { useEffect, useRef, useState } from 'react';

interface ServiceDropdownProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  className?: string;
}

export function ServiceDropdown({
  label,
  value,
  onChange,
  options,
  className = '',
}: ServiceDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full h-[64px] bg-white rounded-[10px] border border-[#eee] hover:border-[#d0c4c5] focus:border-[#8e2a2f] focus:outline-none transition-colors flex flex-col justify-center px-5 pt-3 text-left"
      >
        <span className="absolute left-5 top-[12px] text-[10px] text-[#908e8e] font-['DM_Sans',sans-serif]">
          {label}
        </span>
        <span className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px]">
          {value}
        </span>
        <span
          className={`absolute right-5 top-1/2 -translate-y-1/2 mt-2 material-icons text-[#3a3335] transition-transform ${open ? 'rotate-180' : ''}`}
        >
          expand_more
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 bg-white border border-[#ede8e9] rounded-[10px] shadow-xl py-2 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        >
          {options.map((opt) => {
            const isSelected = opt === value;
            return (
              <li
                key={opt}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className={`px-5 py-3 cursor-pointer font-['DM_Sans',sans-serif] font-bold text-[14px] transition-colors ${
                  isSelected
                    ? 'bg-[#8e2a2f] text-white'
                    : 'text-[#3a3335] hover:bg-[#fcfaf7] hover:text-[#8e2a2f]'
                }`}
              >
                {opt}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
